/**
 * LeadOnboarding.js
 *
 * Opens survey links via @capacitor/browser (SFSafariViewController on iOS,
 * Chrome Custom Tabs on Android) so the lead never leaves the app shell.
 * When the in-app browser is closed the "Already Completed" check fires
 * automatically — no manual button tap needed.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

/* Capacitor Browser plugin — graceful fallback to window.open on web */
let CapBrowser = null;
try {
  // Dynamic require so CRA web builds don't choke if Capacitor isn't loaded
  CapBrowser = require('@capacitor/browser').Browser;
} catch (_) {}

const API = process.env.REACT_APP_API_URL || '';

const SURVEY = {
  medicare: 'https://api.leadconnectorhq.com/widget/survey/JzsherlYv1t85pxsMMQk',
  aca:      'https://api.leadconnectorhq.com/widget/survey/aoHviwYHDCignf8Qxk6p',
};

const LS_KEY = 'leadOnboarding';

const C = {
  brand:   '#1a56db',
  brandBg: '#eff6ff',
  bg:      '#f9fafb',
  card:    '#ffffff',
  border:  '#e5e7eb',
  text:    '#111827',
  muted:   '#6b7280',
  danger:  '#e02424',
  success: '#0e9f6e',
  successBg: '#f0fdf4',
  warn:    '#92400e',
  warnBg:  '#fffbeb',
};

function loadState() {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || '{}'); }
  catch { return {}; }
}
function saveState(partial) {
  try {
    const cur = loadState();
    localStorage.setItem(LS_KEY, JSON.stringify({ ...cur, ...partial }));
  } catch {}
}
function authHeader() {
  const tok = localStorage.getItem('leadToken');
  return tok ? { Authorization: `Bearer ${tok}` } : {};
}

function doLogout() {
  localStorage.removeItem('leadToken');
  localStorage.removeItem('leadOnboarding');
  localStorage.removeItem('leadSignupMethod');
  localStorage.removeItem('leadSignupContact');
  window.location.reload();
}

function Spinner() {
  return (
    <span style={{
      display: 'inline-block', width: 18, height: 18,
      border: '3px solid #dbeafe', borderTopColor: '#1a56db',
      borderRadius: '50%', animation: 'spin .7s linear infinite',
      verticalAlign: 'middle', marginRight: 8,
    }} />
  );
}

function ProgressDots({ step }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 24 }}>
      {[1, 2].map(n => (
        <div key={n} style={{
          width: n === step ? 24 : 8, height: 8, borderRadius: 4,
          background: n === step ? C.brand : (n < step ? C.brand : C.border),
          opacity: n < step ? .4 : 1,
          transition: 'all .3s',
        }} />
      ))}
    </div>
  );
}

export default function LeadOnboarding({ onComplete }) {
  const signupMethod  = localStorage.getItem('leadSignupMethod')  || 'email';
  const signupContact = localStorage.getItem('leadSignupContact') || '';

  const saved = loadState();

  const [step,         setStep]         = useState(saved.step         || 1);
  const [firstName,    setFirstName]    = useState(saved.firstName    || '');
  const [lastName,     setLastName]     = useState(saved.lastName     || '');
  const [contactExtra, setContactExtra] = useState(saved.contactExtra || '');
  const [hasMedicare,  setHasMedicare]  = useState(
    saved.hasMedicare === undefined ? null : saved.hasMedicare
  );

  const [checkLoading,  setCheckLoading]  = useState(false);
  const [checkResult,   setCheckResult]   = useState(null);
  const [checkError,    setCheckError]    = useState('');
  const [formError,     setFormError]     = useState('');
  const [surveyOpened,  setSurveyOpened]  = useState(false);

  // Spin keyframe injection
  useEffect(() => {
    if (!document.getElementById('ob-spin')) {
      const s = document.createElement('style');
      s.id = 'ob-spin';
      s.innerHTML = '@keyframes spin { to { transform: rotate(360deg); } }';
      document.head.appendChild(s);
    }
  }, []);

  // Persist step state
  useEffect(() => {
    saveState({ step, firstName, lastName, contactExtra, hasMedicare });
  }, [step, firstName, lastName, contactExtra, hasMedicare]);

  /* ── Auto-check when in-app browser is dismissed ── */
  /* We store a ref so the listener always has fresh insuranceType */
  const insuranceTypeRef = React.useRef(hasMedicare ? 'medicare' : 'aca');
  const hasMedicareRef   = React.useRef(hasMedicare);
  useEffect(() => {
    insuranceTypeRef.current = hasMedicare ? 'medicare' : 'aca';
    hasMedicareRef.current   = hasMedicare;
  }, [hasMedicare]);

  const autoCheck = useCallback(async () => {
    if (!surveyOpened) return; // only if we actually opened
    setCheckLoading(true);
    setCheckError('');
    setCheckResult(null);
    try {
      const r = await axios.post(
        `${API}/api/lead-auth/check-survey-complete`,
        { insuranceType: insuranceTypeRef.current },
        { headers: authHeader() }
      );
      if (r.data.completed) {
        setCheckResult('completed');
        axios.post(`${API}/api/lead-auth/update-onboarding`,
          { hasMedicare: hasMedicareRef.current, onboardingComplete: true },
          { headers: authHeader() }
        ).then(r2 => { if (r2.data.token) localStorage.setItem('leadToken', r2.data.token); }
        ).catch(() => {});
        localStorage.removeItem(LS_KEY);
      } else {
        setCheckResult('not_completed');
      }
    } catch (_) {
      // silent — user can still tap manually
    } finally {
      setCheckLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyOpened, onComplete]);

  /* Register/unregister the browserFinished listener */
  useEffect(() => {
    if (!CapBrowser) return;
    const handle = CapBrowser.addListener('browserFinished', () => {
      autoCheck();
    });
    return () => {
      handle.then(h => h.remove()).catch(() => {});
    };
  }, [autoCheck]);

  const insuranceType    = hasMedicare ? 'medicare' : 'aca';
  const extraLabel       = signupMethod === 'email' ? 'Phone Number' : 'Email Address';
  const extraType        = signupMethod === 'email' ? 'tel'          : 'email';
  const extraPlaceholder = signupMethod === 'email' ? '+1 (555) 000-0000' : 'you@example.com';

  const handleStep1 = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!firstName.trim()) return setFormError('First name is required.');
    if (!lastName.trim())  return setFormError('Last name is required.');
    const phone = signupMethod === 'email' ? contactExtra : signupContact;
    axios.post(`${API}/api/lead-auth/update-onboarding`, {
      firstName: firstName.trim(), lastName: lastName.trim(),
      phone: phone || undefined,
      email: signupMethod === 'phone' ? contactExtra : undefined,
    }, { headers: authHeader() }).catch(() => {});
    setStep(2);
    window.scrollTo(0, 0);
  };

  const openSurveyWith = async (medicareVal) => {
    const surveyBase = SURVEY[medicareVal ? 'medicare' : 'aca'];
    const first = firstName.trim(), last = lastName.trim();
    const email = signupMethod === 'email' ? signupContact : contactExtra;
    const phone = signupMethod === 'phone' ? signupContact : contactExtra;
    const params = new URLSearchParams({
      ...(first ? { firstName: first } : {}),
      ...(last  ? { lastName:  last  } : {}),
      ...(email ? { email }            : {}),
      ...(phone ? { phone }            : {}),
    });
    const base = surveyBase.includes('?') ? `${surveyBase}&` : `${surveyBase}?`;
    const url  = `${base}${params.toString()}`;

    // Save progress to backend
    axios.post(`${API}/api/lead-auth/update-onboarding`, {
      hasMedicare: medicareVal, onboardingComplete: true,
    }, { headers: authHeader() }).then(r => {
      if (r.data.token) localStorage.setItem('leadToken', r.data.token);
    }).catch(() => {});

    localStorage.removeItem(LS_KEY);
    setSurveyOpened(true);

    if (CapBrowser) {
      // ── In-app browser (SFSafariViewController / Chrome Custom Tabs) ──
      await CapBrowser.open({
        url,
        presentationStyle: 'popover', // iOS: sheet that slides up, easy "Done" dismiss
        toolbarColor: '#1a56db',
      });
    } else {
      // ── Web fallback ──
      window.open(url, '_blank', 'noopener');
    }
  };

  /* Manual "Already Completed" check (backup if auto-check didn't fire) */
  const handleAlreadyCompleted = async () => {
    setCheckLoading(true); setCheckError(''); setCheckResult(null);
    try {
      const r = await axios.post(`${API}/api/lead-auth/check-survey-complete`,
        { insuranceType }, { headers: authHeader() });
      if (r.data.completed) {
        setCheckResult('completed');
        axios.post(`${API}/api/lead-auth/update-onboarding`,
          { hasMedicare, onboardingComplete: true }, { headers: authHeader() }
        ).then(r2 => { if (r2.data.token) localStorage.setItem('leadToken', r2.data.token); }
        ).catch(() => {});
        localStorage.removeItem(LS_KEY);
      } else {
        setCheckResult('not_completed');
      }
    } catch (_) {
      setCheckError('Could not verify at this time. Please try again.');
    } finally { setCheckLoading(false); }
  };

  const inp = {
    width: '100%', padding: '12px 14px', borderRadius: 8,
    border: `1.5px solid ${C.border}`, fontSize: 15,
    boxSizing: 'border-box', outline: 'none', background: '#fff', marginBottom: 12,
  };

  return (
    <div style={{
      minHeight: '100dvh', background: C.bg,
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      padding: '32px 16px 40px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    }}>
      <div style={{ width: '100%', maxWidth: 480 }}>

        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <img src={`${process.env.PUBLIC_URL}/AppLogoLongWithText.png`} alt="My Policy Access" style={{ height: 56, maxWidth: '72%', objectFit: 'contain', marginBottom: 6 }} />
          <h1 style={{ fontSize: 22, fontWeight: 800, color: C.text, margin: '0 0 4px' }}>
            Let's Get You Set Up
          </h1>
          <p style={{ fontSize: 14, color: C.muted, margin: 0 }}>
            Just a few quick questions to personalise your experience
          </p>
        </div>

        <ProgressDots step={step} />

        {/* ─────────────────── STEP 1 ─────────────────── */}
        {step === 1 && (
          <div style={{ background: C.card, borderRadius: 14, border: `1px solid ${C.border}`, padding: '24px 22px' }}>
            <h2 style={{ fontSize: 17, fontWeight: 700, color: C.text, margin: '0 0 18px' }}>
              Tell us about yourself
            </h2>
            {formError && (
              <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: C.danger }}>
                {formError}
              </div>
            )}
            <form onSubmit={handleStep1}>
              <div style={{ display: 'flex', gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 5 }}>First Name *</label>
                  <input type="text" placeholder="First" value={firstName} onChange={e => setFirstName(e.target.value)} style={inp} autoComplete="given-name" />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 5 }}>Last Name *</label>
                  <input type="text" placeholder="Last" value={lastName} onChange={e => setLastName(e.target.value)} style={inp} autoComplete="family-name" />
                </div>
              </div>
              <div style={{ marginBottom: 12, padding: '10px 14px', background: C.bg, borderRadius: 8, border: `1px solid ${C.border}` }}>
                <span style={{ fontSize: 12, color: C.muted }}>{signupMethod === 'email' ? '📧 Email' : '📱 Phone'} (from signup):</span>
                <span style={{ fontSize: 14, color: C.text, marginLeft: 8, fontWeight: 600 }}>{signupContact || '—'}</span>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 5 }}>
                  {extraLabel} <span style={{ color: C.muted, fontWeight: 400 }}>(optional)</span>
                </label>
                <input type={extraType} placeholder={extraPlaceholder} value={contactExtra} onChange={e => setContactExtra(e.target.value)} style={inp} autoComplete={signupMethod === 'email' ? 'tel' : 'email'} />
              </div>
              <button type="submit" style={{ width: '100%', padding: '13px', background: C.brand, color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                Continue →
              </button>
            </form>
          </div>
        )}

        {/* ─────────────────── STEP 2 ─────────────────── */}
        {step === 2 && (
          <div style={{ background: C.card, borderRadius: 14, border: `1px solid ${C.border}`, padding: '24px 22px' }}>
            <button onClick={() => { setStep(1); setCheckResult(null); setSurveyOpened(false); }}
              style={{ background: 'none', border: 'none', color: C.brand, fontSize: 13, cursor: 'pointer', padding: '0 0 16px', fontWeight: 600 }}>
              ← Back
            </button>

            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <img src="/tmpu2434x3j.jpg" alt="Medicare Card"
                style={{ maxWidth: '100%', maxHeight: 180, borderRadius: 10, objectFit: 'contain', boxShadow: '0 2px 12px rgba(0,0,0,.1)' }}
                onError={e => { e.target.style.display = 'none'; }} />
            </div>

            <h2 style={{ fontSize: 20, fontWeight: 800, color: C.text, margin: '0 0 20px', textAlign: 'center' }}>
              Do You Currently Have Medicare?
            </h2>

            {/* Yes / No — opens in-app browser immediately */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
              {[
                { val: true,  label: 'Yes, I Have Medicare',       icon: '✅' },
                { val: false, label: 'No, I Do Not Have Medicare', icon: '❌' },
              ].map(opt => (
                <button key={String(opt.val)}
                  onClick={() => { setHasMedicare(opt.val); setCheckResult(null); setSurveyOpened(false); openSurveyWith(opt.val); }}
                  style={{
                    width: '100%', padding: '16px 20px',
                    background: hasMedicare === opt.val ? C.brand : C.card,
                    color: hasMedicare === opt.val ? '#fff' : C.text,
                    border: `2px solid ${hasMedicare === opt.val ? C.brand : C.border}`,
                    borderRadius: 12, cursor: 'pointer',
                    fontWeight: 700, fontSize: 15,
                    textAlign: 'left',
                    display: 'flex', alignItems: 'center', gap: 12,
                    transition: 'all .15s',
                    boxShadow: hasMedicare === opt.val ? '0 2px 8px rgba(26,86,219,.25)' : 'none',
                  }}>
                  <span style={{ fontSize: 20 }}>{opt.icon}</span>
                  {opt.label}
                </button>
              ))}
            </div>

            <div>
              {/* Survey open hint */}
              {surveyOpened && checkResult === null && !checkLoading && (
                <div style={{ background: C.brandBg, border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: C.brand, lineHeight: 1.5 }}>
                  📋 Complete the survey in the browser that opened, then tap <strong>"Done"</strong> to return — we'll check automatically.
                </div>
              )}

              {/* Auto-checking spinner */}
              {checkLoading && (
                <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: C.muted, textAlign: 'center' }}>
                  <Spinner /> Checking your submission…
                </div>
              )}

              {checkResult === 'completed' && (
                <div style={{ background: C.successBg, border: '1px solid #86efac', borderRadius: 10, padding: '16px 14px', marginBottom: 14, lineHeight: 1.5 }}>
                  <p style={{ fontWeight: 800, fontSize: 16, color: '#166534', margin: '0 0 4px' }}>📋 Existing Application Found</p>
                  <button
                    onClick={() => { if (onComplete) onComplete(); else window.location.href = '/lead/dashboard'; }}
                    style={{ width: '100%', padding: '13px', background: '#0e9f6e', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 15, cursor: 'pointer', marginTop: 10 }}>
                    Continue Your Application →
                  </button>
                </div>
              )}
              {checkResult === 'not_completed' && (
                <div style={{ background: C.warnBg, border: '1px solid #fcd34d', borderRadius: 10, padding: '16px 14px', marginBottom: 14, lineHeight: 1.6 }}>
                  <p style={{ fontWeight: 800, fontSize: 16, color: C.warn, margin: '0 0 6px' }}>📋 Application Not Received Yet</p>
                  <p style={{ fontSize: 13, color: C.warn, margin: '0 0 10px' }}>
                    We haven't received your application yet. Please open the survey, fill it out completely, and tap <strong>Submit</strong> at the end.
                  </p>
                  <button
                    onClick={() => { setCheckResult(null); openSurveyWith(hasMedicare); }}
                    style={{ width: '100%', padding: '11px', background: '#d97706', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
                    📝 Open Application Again
                  </button>
                </div>
              )}
              {checkError && (
                <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: C.danger }}>
                  {checkError}
                </div>
              )}

              {/* Manual fallback check */}
              {surveyOpened && checkResult !== 'completed' && (
                <div style={{ textAlign: 'center', marginTop: 4 }}>
                  <button onClick={handleAlreadyCompleted} disabled={checkLoading}
                    style={{ background: 'none', border: 'none', color: checkLoading ? C.muted : C.brand, fontSize: 13, fontWeight: 600, cursor: checkLoading ? 'not-allowed' : 'pointer', padding: '6px 0', textDecoration: 'underline', textDecorationStyle: 'dotted' }}>
                    {checkLoading ? <><Spinner />Checking…</> : '✔ Already submitted? Tap to verify'}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Footer — large iOS tap target ── */}
        <div style={{ textAlign: 'center', marginTop: 24, paddingBottom: 16 }}>
          <p style={{ fontSize: 12, color: C.muted, margin: '0 0 12px' }}>
            Need help? Contact your insurance agent.
          </p>
          <button
            onClick={doLogout}
            style={{
              display: 'inline-block',
              background: 'none',
              border: `1.5px solid ${C.brand}`,
              borderRadius: 10,
              color: C.brand,
              fontSize: 15,
              fontWeight: 700,
              cursor: 'pointer',
              minHeight: 48,
              minWidth: 180,
              padding: '12px 28px',
              WebkitTapHighlightColor: 'rgba(26,86,219,0.15)',
            }}
          >
            ← Back to login
          </button>
        </div>

      </div>
    </div>
  );
}
